import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaSignOutAlt, FaUserCircle, FaChevronDown, FaHotel } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(52, 152, 219, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 152, 219, 0); }
`;

// Styled Components
const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 3rem;
  background: linear-gradient(135deg, #2c3e50 0%, #1a2530 100%);
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  img {
    height: 42px;
    transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    
    &:hover {
      transform: scale(1.1) rotate(-5deg);
    }
  }
  
  span {
    color: white;
    font-weight: 600;
    font-size: 1.2rem;
    letter-spacing: 0.5px;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  gap: 1.8rem;
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li`
  position: relative;
  
  a {
    color: ${props => props.active ? '#fff' : 'rgba(255,255,255,0.8)'};
    text-decoration: none;
    font-weight: 500;
    font-size: 1rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.9rem;

    &:hover {
      color: #fff;
    }
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.active ? '100%' : '0'};
    height: 3px;
    background: #3498db;
    transition: width 0.4s cubic-bezier(0.65, 0, 0.35, 1);
    border-radius: 3px 3px 0 0;
  }

  &:hover::before {
    width: 100%;
  }
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  cursor: pointer;
  padding: 0.5rem 1rem;
  background: rgba(255,255,255,0.1);
  border-radius: 50px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const UserIcon = styled(FaUserCircle)`
  font-size: 1.6rem;
  color: rgba(255,255,255,0.9);
`;

const UserName = styled.span`
  font-weight: 500;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const LogoutButton = styled.button`
  background: rgba(231, 76, 60, 0.2);
  border: none;
  cursor: pointer;
  color: white;
  font-size: 1.1rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  animation: ${pulse} 2s infinite;

  &:hover {
    background: rgba(231, 76, 60, 0.4);
    transform: scale(1.1);
  }
`;

const GuestButtons = styled.div`
  display: flex;
  gap: 1.2rem;
`;

const AuthButton = styled(Link)`
  padding: 0.6rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  letter-spacing: 0.5px;

  &.login-btn {
    color: white;
    border: 2px solid rgba(255,255,255,0.3);
    
    &:hover {
      border-color: white;
      background: rgba(255,255,255,0.1);
    }
  }

  &.register-btn {
    background: #3498db;
    color: white;
    border: 2px solid #3498db;
    animation: ${pulse} 2s infinite;

    &:hover {
      background: #2980b9;
      border-color: #2980b9;
      transform: translateY(-2px);
    }
  }
`;

const RoleBadge = styled.span`
  background: ${props => {
    switch(props.role) {
      case 'SystemAdmin': return '#e74c3c';
      case 'Manager': return '#2ecc71';
      case 'Staff': return '#f39c12';
      default: return '#3498db';
    }
  }};
  color: white;
  padding: 0.2rem 0.8rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownArrow = styled(FaChevronDown)`
  transition: transform 0.3s ease;
  transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  padding: 0.5rem 0;
  min-width: 200px;
  animation: ${fadeIn} 0.3s ease;
  display: ${props => props.open ? 'block' : 'none'};
`;

const PagesTopNav = () => {
  const location = useLocation();
  const { authData, handleLogout } = useAuth();
  const userRole = authData?.role;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <NavContainer>
      <Logo>
        <Link to="/">
         
          <img src="/images/logo.png" alt="Hotel Management System" />
           
        </Link>
      </Logo>

      <NavLinks>
         
         
      </NavLinks>

      <AuthButtons>
        {authData?.token ? (
          <>
            <UserSection onClick={() => setDropdownOpen(!dropdownOpen)}>
              <UserIcon />
              <UserName>
                {authData?.username || "User"}
                <RoleBadge role={userRole}>{userRole}</RoleBadge>
                <DropdownArrow open={dropdownOpen} size={12} />
              </UserName>
            </UserSection>
            <LogoutButton onClick={handleLogout} title="Logout">
              <FaSignOutAlt />
            </LogoutButton>
            <UserDropdown open={dropdownOpen}>
              {/* Dropdown content would go here */}
            </UserDropdown>
          </>
        ) : (
          <GuestButtons>
            <AuthButton to="/login" className="login-btn">
              Login
            </AuthButton>
            <AuthButton to="/register" className="register-btn">
              Register
            </AuthButton>
          </GuestButtons>
        )}
      </AuthButtons>
    </NavContainer>
  );
};

export default PagesTopNav;